import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import NotFoundPage from '../Errors/NotFound';
import DailyQuests from '../../components/Surveys/DailyQuests';
import PopularSurveys from '../../components/Surveys/PopularSurveys';
import InterestedSurveys from '../../components/Surveys/InterestedSurveys';
import ScorePrice from '../../components/ScorePrice/ScorePrice';
import trackPageView from '../../services/trackPageView';

import '../../assets/styles/layout/wysiwygContent.scss';
import '../../assets/styles/surveys/surveyPage.scss';
import '../../assets/styles/surveys/surveyFinishedPage.scss';

const CHECKBOX_TYPES = new Set(['checkbox', 'check']);

function isQuestionText(question) {
    return question?.type === 'text';
}

function isQuestionMulti(question) {
    return CHECKBOX_TYPES.has(question?.type);
}

function buildInitialAnswers(questions) {
    const result = {};

    (questions || []).forEach((question) => {
        if (isQuestionText(question)) {
            result[question.id] = {text: ''};
            return;
        }

        result[question.id] = {
            selected: isQuestionMulti(question) ? [] : null,
            selfInputs: {},
        };
    });

    return result;
}

function normalizeImageUrl(value) {
    if (!value) {
        return '';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

export default function SurveyItemPage() {
    const {id} = useParams();
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const [data, setData] = useState(null);
    const [metaData, setMetaData] = useState({
        topSurveysByPassedUserCount: [],
        interestedSurveys: [],
        quests: [],
    });
    const [answers, setAnswers] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [requestError, setRequestError] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);

    const loadSurvey = useCallback(() => {
        setLoading(true);
        setNotFound(false);
        setRequestError(false);

        fetch(`/api/v1/surveys/${id}`, {credentials: 'same-origin'})
            .then(async (response) => {
                const payload = await response.json().catch(() => null);

                if (response.status === 401 && payload?.redirectUrl) {
                    history.replace(payload.redirectUrl);
                    return null;
                }

                if (response.status === 404) {
                    setNotFound(true);
                    return null;
                }

                if (!response.ok || !payload) {
                    throw new Error('Failed to load survey page data');
                }

                return payload;
            })
            .then((payload) => {
                if (!payload) {
                    setData(null);
                    return;
                }

                setData(payload);
            })
            .catch(() => {
                setData(null);
                setRequestError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [history, id]);

    useEffect(() => {
        loadSurvey();
    }, [loadSurvey]);

    useEffect(() => {
        fetch('/api/v1/surveys/meta')
            .then((response) => response.json())
            .then((payload) => {
                setMetaData({
                    topSurveysByPassedUserCount: payload.topSurveysByPassedUserCount || [],
                    interestedSurveys: payload.interestedSurveys || [],
                    quests: payload.quests || [],
                });
            })
            .catch(() => {
                setMetaData({
                    topSurveysByPassedUserCount: [],
                    interestedSurveys: [],
                    quests: [],
                });
            });
    }, []);

    useEffect(() => {
        if (!data?.survey?.id) {
            return;
        }

        setAnswers(buildInitialAnswers(data.survey.questions || []));
        setValidationErrors({});
        setSubmitError(false);
        setShowRegistrationPrompt(false);
    }, [data?.survey?.id, data?.survey?.questions]);

    useEffect(() => {
        if (!data?.survey?.id) {
            return;
        }

        trackPageView(`Survey: ${data.survey.title}`);
    }, [data?.survey?.id, data?.survey?.title]);

    useMetaTags({
        title: data?.meta?.title || data?.survey?.title || t('app.title'),
        description: data?.meta?.description || t('app.description'),
        metas: [
            {name: 'keywords', content: data?.meta?.keywords || t('app.keywords')},
        ],
    });

    const backToSurveysUrl = data?.actions?.backToSurveysUrl || '/surveys';
    const loginLink = useMemo(() => ({
        pathname: '/account/login',
        state: {from: location},
    }), [location]);
    const registrationLink = useMemo(() => ({
        pathname: data?.actions?.registrationUrl || '/account/registration',
        state: {from: location},
    }), [data?.actions?.registrationUrl, location]);

    const questionStatistics = useMemo(() => {
        return data?.statistics || {};
    }, [data?.statistics]);

    const setTextAnswer = (questionId, value) => {
        setAnswers((current) => ({
            ...current,
            [questionId]: {
                ...(current[questionId] || {}),
                text: value,
            },
        }));
    };

    const setSingleAnswer = (questionId, answerId) => {
        setAnswers((current) => {
            const state = current[questionId] || {selected: null, selfInputs: {}};
            const nextSelected = state.selected === answerId ? null : answerId;

            return {
                ...current,
                [questionId]: {
                    ...state,
                    selected: nextSelected,
                },
            };
        });
    };

    const setSelectAnswer = (questionId, answerId) => {
        setAnswers((current) => {
            const state = current[questionId] || {selected: null, selfInputs: {}};

            return {
                ...current,
                [questionId]: {
                    ...state,
                    selected: answerId || null,
                },
            };
        });
    };

    const toggleMultipleAnswer = (questionId, answerId) => {
        setAnswers((current) => {
            const state = current[questionId] || {selected: [], selfInputs: {}};
            const selected = Array.isArray(state.selected) ? state.selected : [];
            const normalizedAnswerId = String(answerId);
            const exists = selected.includes(normalizedAnswerId);

            return {
                ...current,
                [questionId]: {
                    ...state,
                    selected: exists
                        ? selected.filter((item) => item !== normalizedAnswerId)
                        : [...selected, normalizedAnswerId],
                },
            };
        });
    };

    const setSelfInput = (questionId, answerId, value) => {
        setAnswers((current) => {
            const state = current[questionId] || {selected: null, selfInputs: {}};
            const selfInputs = state.selfInputs || {};

            return {
                ...current,
                [questionId]: {
                    ...state,
                    selfInputs: {
                        ...selfInputs,
                        [String(answerId)]: value,
                    },
                },
            };
        });
    };

    const validateForm = () => {
        const questions = data?.survey?.questions || [];
        const nextErrors = {};

        questions.forEach((question) => {
            if (!question?.isCompulsory) {
                return;
            }

            const answerState = answers[question.id] || {};

            if (isQuestionText(question)) {
                if (!String(answerState.text || '').trim()) {
                    nextErrors[question.id] = t('surveys.answerRequired');
                }
                return;
            }

            if (isQuestionMulti(question)) {
                const selected = Array.isArray(answerState.selected) ? answerState.selected : [];
                if (!selected.length) {
                    nextErrors[question.id] = t('surveys.answerRequired');
                }
                return;
            }

            if (!answerState.selected) {
                nextErrors[question.id] = t('surveys.answerRequired');
            }
        });

        setValidationErrors(nextErrors);

        return Object.keys(nextErrors).length === 0;
    };

    const buildRequestBody = () => {
        const body = new URLSearchParams();

        (data?.survey?.questions || []).forEach((question) => {
            const answerState = answers[question.id] || {};

            if (isQuestionText(question)) {
                const textAnswer = String(answerState.text || '').trim();
                if (textAnswer !== '') {
                    body.append(`question[${question.id}]`, textAnswer);
                }
                return;
            }

            if (isQuestionMulti(question)) {
                const selected = Array.isArray(answerState.selected) ? answerState.selected : [];
                const selfInputs = answerState.selfInputs || {};

                selected.forEach((answerId) => {
                    body.append(`question[${question.id}][]`, String(answerId));

                    const selfInputValue = String(selfInputs[String(answerId)] || '').trim();
                    if (selfInputValue !== '') {
                        body.append(`question[${question.id}][selfInput][${answerId}]`, selfInputValue);
                    }
                });
                return;
            }

            const selectedAnswer = answerState.selected ? String(answerState.selected) : '';
            if (selectedAnswer === '') {
                return;
            }

            body.append(`question[${question.id}][]`, selectedAnswer);

            const selfInputValue = String(answerState.selfInputs?.[selectedAnswer] || '').trim();
            if (selfInputValue !== '') {
                body.append(`question[${question.id}][selfInput][${selectedAnswer}]`, selfInputValue);
            }
        });

        return body;
    };

    const submitSurvey = (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (data?.isPublic) {
            setShowRegistrationPrompt(true);
            return;
        }

        setSubmitLoading(true);
        setSubmitError(false);

        fetch(data?.actions?.submitUrl || `/api/v1/survey/${id}`, {
            method: 'POST',
            credentials: 'same-origin',
            body: buildRequestBody(),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to submit survey');
                }

                return response;
            })
            .then(() => {
                loadSurvey();
            })
            .catch(() => {
                setSubmitError(true);
            })
            .finally(() => {
                setSubmitLoading(false);
            });
    };

    if (loading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (requestError || !data?.survey) {
        return (
            <section id="quiz">
                <div className="container">
                    <div className="wrap">
                        <h1>{t('errorPages.general.header')}</h1>
                        <div className="link-block">
                            <Link to="/surveys" className="btn">{t('surveys.backToSurveys')}</Link>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    const {survey} = data;

    return (
        <section id="quiz">
            <div className="container">
                <AdvertiseBlock unitId="surveyPage1" blockLocation="top" entityId={survey.id} />

                <div className="row">
                    <nav className="breadcrumbs">
                        <ul>
                            <li>
                                <Link to={backToSurveysUrl}>{t('surveys.allSurveysBreadcrumbs')}</Link>
                            </li>
                            <li className="active" aria-current="page">
                                {survey.title}
                            </li>
                        </ul>
                    </nav>
                </div>

                <div className="survey-grid">
                    <div>
                        <div className="wrap">
                            {data.state === 'finished' ? (
                                <>
                                    <div className="survey-title-block flex">
                                        <div className="title-wrap">
                                            <div className="row">
                                                <h1>{t('surveys.thanksForParticipation')}</h1>
                                                <div className="score-inline js-points">
                                                    {(survey.prices || []).map((price) => (
                                                        <ScorePrice
                                                            key={`${price.currency?.code || 'currency'}-${price.price}`}
                                                            price={price.price}
                                                            currency={price.currency}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {!!survey.sponsors?.length && (
                                            <div className="sponsor-logos">
                                                <h6>{t('surveys.sponsorsCaption')}</h6>
                                                <div className="sponsor-logos-slider">
                                                    {survey.sponsors.map((sponsor) => (
                                                        <div key={sponsor.id}>
                                                            {sponsor.url ? (
                                                                <a href={sponsor.url} target="_blank" rel="noreferrer">
                                                                    <img src={normalizeImageUrl(sponsor.image)} alt="" />
                                                                </a>
                                                            ) : (
                                                                <img src={normalizeImageUrl(sponsor.image)} alt="" />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <h2>{t('surveys.surveyResults')}</h2>
                                    <div className="raiting">
                                        {(survey.questions || []).filter((question) => !isQuestionText(question)).map((question, questionIndex) => (
                                            <div key={question.id}>
                                                <div className="rating-of-this-block">
                                                    <h6>{question.text}</h6>
                                                    {(question.answers || []).map((answer) => {
                                                        const percent = Number(questionStatistics?.[question.id]?.[answer.id] ?? 0);

                                                        return (
                                                            <div className="rating-container" key={answer.id}>
                                                                <div className="percent-number">{percent}%</div>
                                                                <div className="percent-scale">
                                                                    <p>{answer.description}</p>
                                                                    <div className="fill" style={{width: `${Math.max(0, Math.min(100, percent))}%`}}></div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {questionIndex === 1 && (
                                                    <AdvertiseBlock unitId="surveyPage2" blockLocation="content" entityId={survey.id} />
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="link-block">
                                        <Link to={backToSurveysUrl} className="btn">{t('surveys.backToSurveys')}</Link>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="survey-title-block flex">
                                        <div className="title-wrap">
                                            <h1>{survey.title}</h1>
                                            <div className="material-content wysiwyg-content" dangerouslySetInnerHTML={{__html: survey.description || ''}}></div>
                                        </div>

                                        {!!survey.sponsors?.length && !data.isPublic && (
                                            <div className="sponsor-logos">
                                                <h6>{t('surveys.sponsorsCaption')}</h6>
                                                <div className="sponsor-logos-slider">
                                                    {survey.sponsors.map((sponsor) => (
                                                        <div key={sponsor.id}>
                                                            {sponsor.url ? (
                                                                <a href={sponsor.url} target="_blank" rel="noreferrer">
                                                                    <img src={normalizeImageUrl(sponsor.image)} alt="" />
                                                                </a>
                                                            ) : (
                                                                <img src={normalizeImageUrl(sponsor.image)} alt="" />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="survey-form">
                                        <form className="js-active-survey" action="#" method="post" onSubmit={submitSurvey}>
                                            {(survey.questions || []).map((question, questionIndex) => {
                                                const questionState = answers[question.id] || {};
                                                const questionError = validationErrors[question.id] || '';
                                                const selected = questionState.selected;
                                                const selectedArray = Array.isArray(selected) ? selected : [];

                                                return (
                                                    <Fragment key={question.id}>
                                                        {!!question.advertisementBeforeImageUrl && (
                                                            <div className="img-block">
                                                                <img src={normalizeImageUrl(question.advertisementBeforeImageUrl)} alt="" />
                                                            </div>
                                                        )}

                                                        <div className={`form-block ${question.isCompulsory ? 'required js-required' : ''}`}>
                                                            <h6>{question.text}</h6>
                                                            <div className="field_block">
                                                                {isQuestionMulti(question) && (
                                                                    <div className="info-text">{t('surveys.multianswerCaption')}</div>
                                                                )}

                                                                <div className="error-text-block">
                                                                    {!!questionError && (
                                                                        <span className="error-text error_block">{questionError}</span>
                                                                    )}
                                                                </div>

                                                                {!!question.image && (
                                                                    <div className="img-block question">
                                                                        <img src={normalizeImageUrl(question.image)} alt="" />
                                                                    </div>
                                                                )}

                                                                {isQuestionText(question) && (
                                                                    <div className="has-label-animation">
                                                                        <label
                                                                            className={questionState.text ? 'active' : ''}
                                                                            htmlFor={`survey-question-${question.id}`}
                                                                        >
                                                                            {t('surveys.answer')}
                                                                        </label>
                                                                        <input
                                                                            className="text-form"
                                                                            type="text"
                                                                            id={`survey-question-${question.id}`}
                                                                            value={questionState.text || ''}
                                                                            onChange={(event) => setTextAnswer(question.id, event.target.value)}
                                                                        />
                                                                    </div>
                                                                )}

                                                                {!isQuestionText(question) && !isQuestionMulti(question) && (
                                                                    <div>
                                                                        {(question.answers || []).length <= 5 ? (
                                                                            <>
                                                                                {(question.answers || []).map((answer) => {
                                                                                    const answerId = String(answer.id);
                                                                                    const isSelected = String(selected || '') === answerId;
                                                                                    const selfInputValue = questionState.selfInputs?.[answerId] || '';

                                                                                    return (
                                                                                        <Fragment key={answer.id}>
                                                                                            <div className="form-item-block">
                                                                                                <input
                                                                                                    className="magic-radio"
                                                                                                    type="radio"
                                                                                                    id={`survey-question-${question.id}-answer-${answer.id}`}
                                                                                                    checked={isSelected}
                                                                                                    readOnly
                                                                                                    onClick={() => setSingleAnswer(question.id, answerId)}
                                                                                                />
                                                                                                <label htmlFor={`survey-question-${question.id}-answer-${answer.id}`}>{answer.description}</label>
                                                                                            </div>

                                                                                            {!!answer.selfInput && isSelected && (
                                                                                                <div className="has-label-animation js-self-input">
                                                                                                    <label
                                                                                                        className={selfInputValue ? 'active' : ''}
                                                                                                        htmlFor={`survey-question-${question.id}-self-input-${answer.id}`}
                                                                                                    >
                                                                                                        {t('surveys.answer')}
                                                                                                    </label>
                                                                                                    <input
                                                                                                        className="text-form"
                                                                                                        type="text"
                                                                                                        id={`survey-question-${question.id}-self-input-${answer.id}`}
                                                                                                        value={selfInputValue}
                                                                                                        onChange={(event) => setSelfInput(question.id, answerId, event.target.value)}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                        </Fragment>
                                                                                    );
                                                                                })}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <select
                                                                                    className="form-select"
                                                                                    value={selected || ''}
                                                                                    onChange={(event) => setSelectAnswer(question.id, event.target.value)}
                                                                                >
                                                                                    <option value="" disabled>{t('surveys.selectAnswer')}</option>
                                                                                    {(question.answers || []).map((answer) => (
                                                                                        <option key={answer.id} value={String(answer.id)}>{answer.description}</option>
                                                                                    ))}
                                                                                </select>

                                                                                {(question.answers || []).map((answer) => {
                                                                                    const answerId = String(answer.id);
                                                                                    const isSelected = String(selected || '') === answerId;
                                                                                    const selfInputValue = questionState.selfInputs?.[answerId] || '';

                                                                                    if (!answer.selfInput || !isSelected) {
                                                                                        return null;
                                                                                    }

                                                                                    return (
                                                                                        <div key={answer.id} className="has-label-animation js-self-input">
                                                                                            <label
                                                                                                className={selfInputValue ? 'active' : ''}
                                                                                                htmlFor={`survey-question-${question.id}-self-input-${answer.id}`}
                                                                                            >
                                                                                                {t('surveys.answer')}
                                                                                            </label>
                                                                                            <input
                                                                                                className="text-form"
                                                                                                type="text"
                                                                                                id={`survey-question-${question.id}-self-input-${answer.id}`}
                                                                                                value={selfInputValue}
                                                                                                onChange={(event) => setSelfInput(question.id, answerId, event.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {isQuestionMulti(question) && (
                                                                    <div>
                                                                        {(question.answers || []).map((answer) => {
                                                                            const answerId = String(answer.id);
                                                                            const isSelected = selectedArray.includes(answerId);
                                                                            const selfInputValue = questionState.selfInputs?.[answerId] || '';

                                                                            return (
                                                                                <Fragment key={answer.id}>
                                                                                    <div className="form-item-block">
                                                                                        <input
                                                                                            className="magic-checkbox"
                                                                                            type="checkbox"
                                                                                            id={`survey-question-${question.id}-answer-${answer.id}`}
                                                                                            checked={isSelected}
                                                                                            onChange={() => toggleMultipleAnswer(question.id, answerId)}
                                                                                        />
                                                                                        <label htmlFor={`survey-question-${question.id}-answer-${answer.id}`}>{answer.description}</label>
                                                                                    </div>

                                                                                    {!!answer.selfInput && isSelected && (
                                                                                        <div className="has-label-animation js-self-input">
                                                                                            <label
                                                                                                className={selfInputValue ? 'active' : ''}
                                                                                                htmlFor={`survey-question-${question.id}-self-input-${answer.id}`}
                                                                                            >
                                                                                                {t('surveys.answer')}
                                                                                            </label>
                                                                                            <input
                                                                                                className="text-form"
                                                                                                type="text"
                                                                                                id={`survey-question-${question.id}-self-input-${answer.id}`}
                                                                                                value={selfInputValue}
                                                                                                onChange={(event) => setSelfInput(question.id, answerId, event.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </Fragment>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {!!question.advertisementAfterImageUrl && (
                                                                <div className="img-block after">
                                                                    <img src={normalizeImageUrl(question.advertisementAfterImageUrl)} alt="" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {((survey.questions || []).length < 3 && questionIndex === (survey.questions || []).length - 1) || questionIndex === 2 ? (
                                                            <AdvertiseBlock unitId="surveyPage2" blockLocation="content" entityId={survey.id} />
                                                        ) : null}
                                                    </Fragment>
                                                );
                                            })}

                                            <div className="submit-block">
                                                <button type="submit" className="btn btn-submit" disabled={submitLoading}>
                                                    {t('surveys.submit')}
                                                </button>

                                                {submitError && (
                                                    <div className="error-text-block">
                                                        <span className="error-text error_block">{t('errorPages.general.header')}</span>
                                                    </div>
                                                )}

                                            </div>
                                        </form>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div>
                        {!!metaData.quests?.length && !data.isPublic && <DailyQuests quests={metaData.quests} />}
                        <AdvertiseBlock unitId="surveyPage3" blockLocation="sidebar" entityId={survey.id} />
                        {!!metaData.topSurveysByPassedUserCount?.length && <PopularSurveys surveys={metaData.topSurveysByPassedUserCount} />}
                        {!!metaData.interestedSurveys?.length && !data.isPublic && <InterestedSurveys surveys={metaData.interestedSurveys} />}
                    </div>
                </div>
            </div>

            {showRegistrationPrompt && (
                <div
                    className="public-popup-register-overlay"
                    role="presentation"
                    onClick={() => setShowRegistrationPrompt(false)}
                >
                    <div
                        className="public-popup-register"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="survey-registration-prompt-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="public-popup-register__close"
                            aria-label={t('surveys.browser.close')}
                            onClick={() => setShowRegistrationPrompt(false)}
                        >
                            ×
                        </button>

                        <h2 id="survey-registration-prompt-title">{t('surveys.registrationRequiredCaption')}</h2>

                        <div className="button-block">
                            <Link to={loginLink} className="btn-register btn-login">
                                {t('publicAuthPopup.login')}
                            </Link>
                            <Link to={registrationLink} className="btn-register">
                                {t('publicAuthPopup.register')}
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
